import React, { useState, useRef } from 'react';
import { Music, Wand2, Headphones, Search, Play, Pause, Upload, Edit2, Trash2, Volume2 } from 'lucide-react';

interface AudioTrack {
  id: string;
  title: string;
  artist: string;
  duration: string;
  thumbnail: string;
  category?: string;
  tags?: string[];
  waveform?: number[];
}

interface AudioBarProps {
  onAudioSelect?: (track: AudioTrack | null) => void;
}

export default function AudioBar({ onAudioSelect }: AudioBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'music' | 'effects' | 'ai'>('music');
  const [selectedAudio, setSelectedAudio] = useState<AudioTrack | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiQuery, setAiQuery] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mock trending music data
  const trendingMusic: AudioTrack[] = [
    {
      id: 'm1',
      title: 'Summer Vibes',
      artist: 'DJ Wave',
      duration: '2:34',
      thumbnail: '🎵',
      tags: ['upbeat', 'summer', 'pop'],
      waveform: [0.3, 0.5, 0.7, 0.9, 0.6, 0.4, 0.8, 0.5, 0.3, 0.6, 0.7, 0.4, 0.5, 0.8, 0.6, 0.3]
    },
    {
      id: 'm2',
      title: 'Chill Lofi Beats',
      artist: 'Lofi Studio',
      duration: '3:12',
      thumbnail: '🎧',
      tags: ['chill', 'lofi', 'relaxing'],
      waveform: [0.2, 0.4, 0.3, 0.5, 0.4, 0.3, 0.5, 0.4, 0.3, 0.4, 0.5, 0.3, 0.4, 0.5, 0.4, 0.2]
    },
    {
      id: 'm3',
      title: 'Energetic Rock',
      artist: 'The Rockers',
      duration: '2:48',
      thumbnail: '🎸',
      tags: ['rock', 'energetic', 'guitar'],
      waveform: [0.6, 0.8, 0.9, 1.0, 0.8, 0.7, 0.9, 0.8, 0.6, 0.8, 0.9, 0.7, 0.8, 0.9, 0.8, 0.6]
    },
    {
      id: 'm4',
      title: 'Corporate Motivation',
      artist: 'Pro Sounds',
      duration: '2:20',
      thumbnail: '💼',
      tags: ['corporate', 'motivational', 'uplifting'],
      waveform: [0.4, 0.6, 0.7, 0.8, 0.6, 0.5, 0.7, 0.6, 0.4, 0.6, 0.7, 0.5, 0.6, 0.7, 0.6, 0.4]
    },
    {
      id: 'm5',
      title: 'Jazz Piano',
      artist: 'Smooth Jazz Co',
      duration: '3:45',
      thumbnail: '🎹',
      tags: ['jazz', 'piano', 'smooth'],
      waveform: [0.3, 0.4, 0.5, 0.6, 0.5, 0.4, 0.5, 0.4, 0.3, 0.5, 0.6, 0.4, 0.5, 0.6, 0.5, 0.3]
    },
    {
      id: 'm6',
      title: 'Electronic Dance',
      artist: 'EDM Masters',
      duration: '3:00',
      thumbnail: '🎛️',
      tags: ['edm', 'dance', 'electronic'],
      waveform: [0.7, 0.9, 1.0, 0.8, 0.9, 0.7, 0.8, 0.9, 0.7, 0.9, 1.0, 0.8, 0.9, 0.8, 0.9, 0.7]
    }
  ];

  // Mock sound effects data
  const soundEffects: AudioTrack[] = [
    { id: 'sfx1', title: 'Swoosh Transition', artist: 'SFX Library', duration: '0:02', thumbnail: '💨', category: 'Transitions' },
    { id: 'sfx2', title: 'Pop Notification', artist: 'SFX Library', duration: '0:01', thumbnail: '🔔', category: 'Notifications' },
    { id: 'sfx3', title: 'Success Chime', artist: 'SFX Library', duration: '0:03', thumbnail: '✨', category: 'UI Sounds' },
    { id: 'sfx4', title: 'Camera Shutter', artist: 'SFX Library', duration: '0:01', thumbnail: '📸', category: 'Camera' },
    { id: 'sfx5', title: 'Applause', artist: 'SFX Library', duration: '0:05', thumbnail: '👏', category: 'Crowd' },
    { id: 'sfx6', title: 'Whoosh Impact', artist: 'SFX Library', duration: '0:02', thumbnail: '💥', category: 'Transitions' },
    { id: 'sfx7', title: 'Bell Ring', artist: 'SFX Library', duration: '0:02', thumbnail: '🔔', category: 'Notifications' },
    { id: 'sfx8', title: 'Keyboard Type', artist: 'SFX Library', duration: '0:03', thumbnail: '⌨️', category: 'UI Sounds' }
  ];

  const categories = ['all', 'Transitions', 'Notifications', 'UI Sounds', 'Camera', 'Crowd'];

  // Mock AI suggestions
  const aiSuggestions: AudioTrack[] = [
    { id: 'ai1', title: 'Upbeat Adventure', artist: 'AI Generated', duration: '2:45', thumbnail: '🤖', tags: ['upbeat', 'adventure'] },
    { id: 'ai2', title: 'Calm Nature', artist: 'AI Generated', duration: '3:10', thumbnail: '🌿', tags: ['calm', 'nature'] },
    { id: 'ai3', title: 'Tech Innovation', artist: 'AI Generated', duration: '2:30', thumbnail: '💡', tags: ['tech', 'innovation'] }
  ];

  const filteredMusic = trendingMusic.filter(track =>
    track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    track.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
    track.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredEffects = selectedCategory === 'all'
    ? soundEffects
    : soundEffects.filter(sfx => sfx.category === selectedCategory);

  const handleSelectTrack = (track: AudioTrack) => {
    setSelectedAudio(track);
    setIsExpanded(false);
    onAudioSelect?.(track);
  };

  const handleRemoveAudio = () => {
    setSelectedAudio(null);
    onAudioSelect?.(null);
  };

  const togglePlay = (id: string) => {
    setPlayingId(playingId === id ? null : id);
  };

  return (
    <div className="w-full">
      {/* Collapsed State - Add Audio Button or Selected Audio Chip */}
      {!selectedAudio ? (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-4 border-2 border-dashed border-[#E5E7EB] hover:border-[#F67570] rounded-lg transition-all bg-gradient-to-br from-white to-gray-50 group"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F67570] to-[#F99B97] flex items-center justify-center text-white shadow-md group-hover:shadow-lg transition-shadow">
              <Music size={20} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#111827]">Add Audio to Post</div>
              <div className="text-xs text-[#6B7280]">Music, sound effects, or AI-generated tracks</div>
            </div>
          </div>
        </button>
      ) : (
        <div className="p-4 border border-[#E5E7EB] rounded-lg bg-white shadow-sm">
          <div className="flex items-center gap-4">
            {/* Waveform Preview */}
            <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gradient-to-br from-[#F67570]/10 to-[#F99B97]/10 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 flex items-end justify-around p-2 gap-0.5">
                {selectedAudio.waveform ? (
                  selectedAudio.waveform.slice(0, 12).map((height, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-[#F67570] to-[#F99B97] rounded-sm transition-all"
                      style={{ height: `${height * 100}%` }}
                    />
                  ))
                ) : (
                  <Volume2 size={24} className="text-[#F67570]" />
                )}
              </div>
            </div>

            {/* Track Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{selectedAudio.thumbnail}</span>
                <div className="text-sm font-semibold text-[#111827] truncate">
                  {selectedAudio.title}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                <span>{selectedAudio.artist}</span>
                <span>•</span>
                <span>{selectedAudio.duration}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExpanded(true)}
                className="p-2 hover:bg-[#F9FAFB] rounded-md transition-colors"
                title="Replace audio"
              >
                <Edit2 size={16} className="text-[#6B7280]" />
              </button>
              <button
                onClick={handleRemoveAudio}
                className="p-2 hover:bg-[#FEE2E2] rounded-md transition-colors"
                title="Remove audio"
              >
                <Trash2 size={16} className="text-[#EF4444]" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded State - Audio Selection Drawer */}
      {isExpanded && (
        <div className="mt-4 border border-[#E5E7EB] rounded-lg bg-white shadow-lg overflow-hidden animate-slideDown">
          {/* Tab Navigation */}
          <div className="border-b border-[#E5E7EB] bg-gradient-to-r from-gray-50 to-white">
            <div className="flex">
              <button
                onClick={() => setActiveTab('music')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-all relative ${
                  activeTab === 'music'
                    ? 'text-[#F67570] bg-white'
                    : 'text-[#6B7280] hover:text-[#111827] hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Music size={18} />
                  <span>Trending Music</span>
                </div>
                {activeTab === 'music' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F67570] to-[#F99B97]" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('effects')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-all relative ${
                  activeTab === 'effects'
                    ? 'text-[#F67570] bg-white'
                    : 'text-[#6B7280] hover:text-[#111827] hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Headphones size={18} />
                  <span>Sound Effects</span>
                </div>
                {activeTab === 'effects' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F67570] to-[#F99B97]" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('ai')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-all relative ${
                  activeTab === 'ai'
                    ? 'text-[#F67570] bg-white'
                    : 'text-[#6B7280] hover:text-[#111827] hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Wand2 size={18} />
                  <span>AI Recognizer</span>
                </div>
                {activeTab === 'ai' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F67570] to-[#F99B97]" />
                )}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6 max-h-[480px] overflow-y-auto">
            {/* Trending Music Tab */}
            {activeTab === 'music' && (
              <div>
                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search music by title, artist, or mood..."
                      className="w-full h-10 pl-10 pr-3 text-sm border border-[#D1D5DB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#FED7D5] transition-all"
                    />
                  </div>
                </div>

                {/* Music List */}
                <div className="space-y-2">
                  {filteredMusic.map((track) => (
                    <div
                      key={track.id}
                      className="group p-3 border border-[#E5E7EB] hover:border-[#F67570] rounded-lg transition-all hover:shadow-md bg-white"
                    >
                      <div className="flex items-center gap-3">
                        {/* Thumbnail */}
                        <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-gradient-to-br from-[#F67570]/20 to-[#F99B97]/20 flex items-center justify-center text-2xl">
                          {track.thumbnail}
                        </div>

                        {/* Track Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#111827] mb-1">
                            {track.title}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                            <span>{track.artist}</span>
                            <span>•</span>
                            <span>{track.duration}</span>
                          </div>
                          {track.tags && (
                            <div className="flex gap-1 mt-1">
                              {track.tags.slice(0, 3).map((tag, i) => (
                                <span key={i} className="px-2 py-0.5 bg-[#F9FAFB] text-[#6B7280] text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => togglePlay(track.id)}
                            className="w-8 h-8 rounded-full bg-[#F9FAFB] hover:bg-[#F67570] hover:text-white flex items-center justify-center transition-colors"
                            title="Preview"
                          >
                            {playingId === track.id ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                          <button
                            onClick={() => handleSelectTrack(track)}
                            className="px-3 py-1.5 bg-gradient-to-r from-[#F67570] to-[#F99B97] text-white text-xs font-medium rounded-md hover:shadow-md transition-all"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sound Effects Tab */}
            {activeTab === 'effects' && (
              <div>
                {/* Category Filter */}
                <div className="mb-4">
                  <div className="flex gap-2 flex-wrap">
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                          selectedCategory === cat
                            ? 'bg-gradient-to-r from-[#F67570] to-[#F99B97] text-white shadow-md'
                            : 'bg-[#F9FAFB] text-[#6B7280] hover:bg-[#E5E7EB]'
                        }`}
                      >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Effects Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {filteredEffects.map((sfx) => (
                    <div
                      key={sfx.id}
                      className="p-3 border border-[#E5E7EB] hover:border-[#F67570] rounded-lg transition-all hover:shadow-md bg-white group"
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F67570]/20 to-[#F99B97]/20 flex items-center justify-center text-xl">
                            {sfx.thumbnail}
                          </div>
                          <button
                            onClick={() => togglePlay(sfx.id)}
                            className="w-7 h-7 rounded-full bg-[#F9FAFB] hover:bg-[#F67570] hover:text-white flex items-center justify-center transition-colors"
                          >
                            {playingId === sfx.id ? <Pause size={12} /> : <Play size={12} />}
                          </button>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#111827] mb-1 truncate">
                            {sfx.title}
                          </div>
                          <div className="text-xs text-[#6B7280] mb-2">{sfx.duration}</div>
                          <button
                            onClick={() => handleSelectTrack(sfx)}
                            className="w-full px-3 py-1.5 bg-gradient-to-r from-[#F67570] to-[#F99B97] text-white text-xs font-medium rounded-md hover:shadow-md transition-all"
                          >
                            Add Effect
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Recognizer Tab */}
            {activeTab === 'ai' && (
              <div>
                {/* AI Input */}
                <div className="mb-6">
                  <div className="p-6 border-2 border-dashed border-[#E5E7EB] rounded-lg bg-gradient-to-br from-purple-50 to-blue-50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white">
                        <Wand2 size={20} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#111827]">AI Sound Recognition</div>
                        <div className="text-xs text-[#6B7280]">Describe the audio you need or upload a sample</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <textarea
                        value={aiQuery}
                        onChange={(e) => setAiQuery(e.target.value)}
                        placeholder="Describe the mood, genre, or feeling... (e.g., 'upbeat summer party music' or 'calm meditation background')"
                        className="w-full h-20 p-3 text-sm border border-[#D1D5DB] rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
                      />

                      <div className="flex gap-2">
                        <button className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all">
                          <div className="flex items-center justify-center gap-2">
                            <Wand2 size={16} />
                            <span>Generate Suggestions</span>
                          </div>
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="audio/*"
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="px-4 py-2 border border-[#D1D5DB] text-[#6B7280] text-sm font-medium rounded-lg hover:bg-[#F9FAFB] transition-all"
                        >
                          <div className="flex items-center gap-2">
                            <Upload size={16} />
                            <span>Upload Sample</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Suggestions */}
                <div>
                  <h3 className="text-sm font-semibold text-[#111827] mb-3">AI-Generated Suggestions</h3>
                  <div className="space-y-2">
                    {aiSuggestions.map((track) => (
                      <div
                        key={track.id}
                        className="p-3 border border-[#E5E7EB] hover:border-purple-500 rounded-lg transition-all hover:shadow-md bg-white group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-2xl">
                            {track.thumbnail}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#111827] mb-1">
                              {track.title}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                              <span>{track.artist}</span>
                              <span>•</span>
                              <span>{track.duration}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => togglePlay(track.id)}
                              className="w-8 h-8 rounded-full bg-[#F9FAFB] hover:bg-purple-500 hover:text-white flex items-center justify-center transition-colors"
                            >
                              {playingId === track.id ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                            <button
                              onClick={() => handleSelectTrack(track)}
                              className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-medium rounded-md hover:shadow-md transition-all"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#E5E7EB] p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-xs text-[#6B7280]">
                {activeTab === 'music' && `${filteredMusic.length} tracks available`}
                {activeTab === 'effects' && `${filteredEffects.length} sound effects`}
                {activeTab === 'ai' && 'Powered by AI audio recognition'}
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="px-4 py-2 text-sm font-medium text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
